import React from 'react';
import { Typography, Card, CardContent, Box, IconButton, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useLocales } from '../../../hooks';
import { CheckCircle, SwapHoriz } from '@mui/icons-material';
import { useRecoilState, useRecoilValue } from 'recoil';
import { withLayoutComparisonData, withSelectedLayoutsForComparison } from '../../../state/Layouts';
import Localized from '../../shared/Localized';
import { LayoutComparisonSummary } from './LayoutComparisonSummary';
import { LayoutComparisonModuleChanges } from './LayoutComparisonModuleChanges';
import { ComparisonData } from '../../../state/Layouts';

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(4),
    overflow: 'auto'
  },
  header: {
    marginBottom: theme.spacing(4),
    textAlign: 'center'
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2)
  },
  changeCard: {
    marginBottom: theme.spacing(2)
  },
  noChanges: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary
  },
  swapButton: {
    padding: theme.spacing(1),
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  }
}));

interface LayoutComparisonProps {
  comparisonData: ComparisonData | null;
}

export function LayoutComparison({ comparisonData }: LayoutComparisonProps): React.ReactElement {
  const { classes } = useStyles();
  const { t } = useLocales();
  const comparisonDataState = useRecoilValue(withLayoutComparisonData);
  const [selectedLayoutsForComparison, setSelectedLayoutsForComparison] = useRecoilState(
    withSelectedLayoutsForComparison
  );

  // Use the prop data if provided, otherwise fall back to Recoil state
  const activeComparisonData = comparisonData || comparisonDataState;

  const handleSwapLayouts = () => {
    if (selectedLayoutsForComparison.length === 2) {
      // Swap the order of layout IDs to trigger a new comparison request
      setSelectedLayoutsForComparison([selectedLayoutsForComparison[1], selectedLayoutsForComparison[0]]);
    }
  };

  if (!activeComparisonData) {
    return (
      <div className={classes.root}>
        <Typography variant="h6" className={classes.noChanges}>
          {t('layouts.no_comparison_data')}
        </Typography>
      </div>
    );
  }

  const { sourceLayout, targetLayout, metadataChanges, moduleChanges, summary } = activeComparisonData;

  const hasChanges = (metadataChanges && metadataChanges.length > 0) || (moduleChanges && moduleChanges.length > 0);
  const sourceLayoutName = sourceLayout?.pageName || {
    en: t('layouts.source_layout'),
    es: t('layouts.source_layout'),
    pt: t('layouts.source_layout')
  };
  const targetLayoutName = targetLayout?.pageName || {
    en: t('layouts.target_layout'),
    es: t('layouts.target_layout'),
    pt: t('layouts.target_layout')
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h4" gutterBottom>
          {t('layouts.layout_comparison')}
        </Typography>
        <div className={classes.headerRow}>
          <Localized prop={sourceLayoutName} />
          <Tooltip title={t('layouts.swap_comparison')} arrow>
            <IconButton
              className={classes.swapButton}
              onClick={handleSwapLayouts}
              size="small"
              color="secondary"
              data-testid="swap-comparison-button"
            >
              <SwapHoriz />
            </IconButton>
          </Tooltip>
          <Localized prop={targetLayoutName} />
        </div>
      </div>

      {/* Summary Card */}
      <LayoutComparisonSummary
        summary={summary}
        sourceLayout={sourceLayout}
        targetLayout={targetLayout}
        metadataChanges={metadataChanges}
      />

      {/* Module Changes Only */}
      {moduleChanges && moduleChanges.length > 0 && (
        <LayoutComparisonModuleChanges
          moduleChanges={moduleChanges}
          sourceLayout={sourceLayout}
          targetLayout={targetLayout}
        />
      )}

      {/* No Changes Message */}
      {!hasChanges && (
        <Card className={classes.changeCard}>
          <CardContent>
            <Box className={classes.noChanges}>
              <CheckCircle style={{ fontSize: 48, color: '#4caf50', marginBottom: 16 }} />
              <Typography variant="h6">{t('layouts.no_changes_detected')}</Typography>
              <Typography variant="body2" color="textSecondary">
                {t('layouts.layouts_are_identical')}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
